import { Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, } from "@mui/material";
import { Fragment } from "react";
import {
    useAppSidePanelContextProvider
} from "../../../../shared/contexts/app-side-panel-context-provider/components/AppSidePanelContextProvider";
import { PortfolioBalancerItemModel } from "./PortfolioBalancerItemModel";

export function PortfolioBalancer() {
    const {
        marketDataDto,
        selectedPortfolio,
        portfolioProfile,
    } = useAppSidePanelContextProvider();
    
    const deltaPriceStep = 1000;
    const calculationCount = 5;
    
    const getCurrentShortBalance = (multiplier: number): number => {
        if (!marketDataDto || !marketDataDto.UnderlyingPrice || !selectedPortfolio || !selectedPortfolio.portfolioSettings)
            return 0;
        
        const shortPriceShift = 0 - deltaPriceStep * multiplier;
        const targetPrice = selectedPortfolio.portfolioSettings.useCustomPrice
            ? selectedPortfolio.portfolioSettings.customPrice + shortPriceShift
            : marketDataDto.UnderlyingPrice + shortPriceShift;

        const records = portfolioProfile.data;
        
        for (let i = 0; i < records.length - 1; i++) {
            if (targetPrice >= records[i].price && targetPrice < records[i + 1].price) {
                const deltaToLowerPrice = targetPrice - records[i].price;
                const deltaToHigherPrice = records[i + 1].price - targetPrice;
                
                return deltaToLowerPrice <= deltaToHigherPrice
                    ? records[i].currentDateBalance
                    : records[i + 1].currentDateBalance;
            }
        }
        
        return 0;
    }

    const getExpirationLongBalance = (multiplier: number): number => {
        if (!marketDataDto || !marketDataDto.UnderlyingPrice || !selectedPortfolio || !selectedPortfolio.portfolioSettings)
            return 0;

        const longPriceShift = deltaPriceStep * multiplier;
        const targetPrice = selectedPortfolio.portfolioSettings.useCustomPrice
            ? selectedPortfolio.portfolioSettings.customPrice + longPriceShift
            : marketDataDto.UnderlyingPrice + longPriceShift;

        const records = portfolioProfile.data;

        for (let i = 0; i < records.length - 1; i++) {
            if (targetPrice >= records[i].price && targetPrice < records[i + 1].price) {
                const deltaToLowerPrice = targetPrice - records[i].price;
                const deltaToHigherPrice = records[i + 1].price - targetPrice;

                return deltaToLowerPrice <= deltaToHigherPrice
                    ? records[i].expirationDateBalance
                    : records[i + 1].expirationDateBalance;
            }
        }

        return 0;
    }
    
    const getCalculatedModels = (): PortfolioBalancerItemModel[] => {
        if (!marketDataDto || !marketDataDto.UnderlyingPrice || !selectedPortfolio || !selectedPortfolio.portfolioSettings)
            return [];
        
        const result: PortfolioBalancerItemModel[] = [];

        const targetPrice = selectedPortfolio.portfolioSettings.useCustomPrice
            ? selectedPortfolio.portfolioSettings.customPrice
            : marketDataDto.UnderlyingPrice;
        
        for (let multiplier = 0; multiplier <= calculationCount; multiplier++) {
            const currentShortBalance = getCurrentShortBalance(multiplier);
            const expirationLongBalance = getExpirationLongBalance(multiplier);
            const currentShortDelta = multiplier != 0
                ? currentShortBalance - result[0].currentShortBalance
                : 0;
            const expirationLongDelta = multiplier != 0
                ? expirationLongBalance - result[0].expirationLongBalance
                : 0;
            const disbalance = currentShortDelta - expirationLongDelta;
            const balancingOrderSize = (disbalance / 2) / (deltaPriceStep * multiplier);
            
            const model: PortfolioBalancerItemModel = {
                multiplier: multiplier,
                deltaPrice: deltaPriceStep * multiplier,
                shortPrice: targetPrice - deltaPriceStep * multiplier,
                longPrice: targetPrice + deltaPriceStep * multiplier,
                currentShortBalance: currentShortBalance,
                expirationLongBalance : expirationLongBalance,
                currentShortDelta: currentShortDelta,
                expirationLongDelta: expirationLongDelta,
                shortBalance: currentShortDelta - disbalance / 2,
                disbalance: disbalance,
                balancingOrderSize: balancingOrderSize,
            };

            result.push(model);
        }
        
        return result;
    }
    
    const models = getCalculatedModels();
    
    const tablePadding: number = 4;
    
    return (
        <TableContainer>
            <Table sx={{ width: 'auto' }} size="small" aria-label="caption table">
                <TableHead>
                    <TableRow>
                        <Tooltip
                            placement="bottom-start"
                            arrow
                            componentsProps={{
                                tooltip: {
                                    sx: {
                                        maxWidth: '300px'
                                    },
                                },
                            }}
                            slotProps={{
                                popper: {
                                    modifiers: [
                                        {
                                            name: 'offset',
                                            options: {
                                                offset: [0, -15],
                                            },
                                        },
                                    ],
                                },
                            }}
                            title={
                                <Fragment>
                                    <Box sx={{fontSize: "13px"}}>The price step that measures shift from the current price</Box>
                                </Fragment>
                            }
                        >
                            <TableCell
                                align="right"
                                style={{ fontWeight: 'bold', fontSize: 12, padding: tablePadding }}
                            >
                                Price ±
                            </TableCell>
                        </Tooltip>
                        <Tooltip
                            placement="bottom-start"
                            arrow
                            componentsProps={{
                                tooltip: {
                                    sx: {
                                        maxWidth: '300px'
                                    },
                                },
                            }}
                            slotProps={{
                                popper: {
                                    modifiers: [
                                        {
                                            name: 'offset',
                                            options: {
                                                offset: [0, -15],
                                            },
                                        },
                                    ],
                                },
                            }}
                            title={
                                <Fragment>
                                    <Box sx={{fontSize: "13px"}}>
                                        The underlying price to the lower direction from the current one according to the step value
                                    </Box>
                                </Fragment>
                            }
                        >
                            <TableCell
                                align="right"
                                style={{ fontWeight: 'bold', fontSize: 12, padding: tablePadding }}
                            >
                                Short price
                            </TableCell>
                        </Tooltip>
                        <Tooltip
                            placement="bottom-start"
                            arrow
                            componentsProps={{
                                tooltip: {
                                    sx: {
                                        maxWidth: '300px'
                                    },
                                },
                            }}
                            slotProps={{
                                popper: {
                                    modifiers: [
                                        {
                                            name: 'offset',
                                            options: {
                                                offset: [0, -15],
                                            },
                                        },
                                    ],
                                },
                            }}
                            title={
                                <Fragment>
                                    <Box sx={{fontSize: "13px"}}>
                                        The underlying price to the higher direction from the current one according to the step value
                                    </Box>
                                </Fragment>
                            }
                        >
                            <TableCell
                                align="right"
                                style={{ fontWeight: 'bold', fontSize: 12, padding: tablePadding }}
                            >
                                Long price
                            </TableCell>
                        </Tooltip>
                        <Tooltip
                            placement="bottom-start"
                            arrow
                            componentsProps={{
                                tooltip: {
                                    sx: {
                                        maxWidth: '300px'
                                    },
                                },
                            }}
                            slotProps={{
                                popper: {
                                    modifiers: [
                                        {
                                            name: 'offset',
                                            options: {
                                                offset: [0, -15],
                                            },
                                        },
                                    ],
                                },
                            }}
                            title={
                                <Fragment>
                                    <Box sx={{fontSize: "13px"}}>
                                        Portfolio current date balance on the 'Sort price' point
                                    </Box>
                                </Fragment>
                            }
                        >
                            <TableCell
                                align="right"
                                style={{ fontWeight: 'bold', fontSize: 12, padding: tablePadding }}
                            >
                                Cur short
                            </TableCell>
                        </Tooltip>
                        <Tooltip
                            placement="bottom-start"
                            arrow
                            componentsProps={{
                                tooltip: {
                                    sx: {
                                        maxWidth: '300px'
                                    },
                                },
                            }}
                            slotProps={{
                                popper: {
                                    modifiers: [
                                        {
                                            name: 'offset',
                                            options: {
                                                offset: [0, -15],
                                            },
                                        },
                                    ],
                                },
                            }}
                            title={
                                <Fragment>
                                    <Box sx={{fontSize: "13px"}}>
                                        Portfolio expiration date balance on the 'Long price' point
                                    </Box>
                                </Fragment>
                            }
                        >
                            <TableCell
                                align="right"
                                style={{ fontWeight: 'bold', fontSize: 12, padding: tablePadding }}
                            >
                                Exp long
                            </TableCell>
                        </Tooltip>
                        <Tooltip
                            placement="bottom-start"
                            arrow
                            componentsProps={{
                                tooltip: {
                                    sx: {
                                        maxWidth: '300px'
                                    },
                                },
                            }}
                            slotProps={{
                                popper: {
                                    modifiers: [
                                        {
                                            name: 'offset',
                                            options: {
                                                offset: [0, -15],
                                            },
                                        },
                                    ],
                                },
                            }}
                            title={
                                <Fragment>
                                    <Box sx={{fontSize: "13px"}}>
                                        Trading result between the current price and the current date price on the 'Short price' point
                                    </Box>
                                </Fragment>
                            }
                        >
                            <TableCell
                                align="right"
                                style={{ fontWeight: 'bold', fontSize: 12, padding: tablePadding }}
                            >
                                Cur delta
                            </TableCell>
                        </Tooltip>
                        <Tooltip
                            placement="bottom-start"
                            arrow
                            componentsProps={{
                                tooltip: {
                                    sx: {
                                        maxWidth: '300px'
                                    },
                                },
                            }}
                            slotProps={{
                                popper: {
                                    modifiers: [
                                        {
                                            name: 'offset',
                                            options: {
                                                offset: [0, -15],
                                            },
                                        },
                                    ],
                                },
                            }}
                            title={
                                <Fragment>
                                    <Box sx={{fontSize: "13px"}}>
                                        Trading result between the current price and the expiration date price on the 'Long price' point
                                    </Box>
                                </Fragment>
                            }
                        >
                            <TableCell
                                align="right"
                                style={{ fontWeight: 'bold', fontSize: 12, padding: tablePadding }}
                            >
                                Exp delta
                            </TableCell>
                        </Tooltip>
                        <Tooltip
                            placement="bottom-start"
                            arrow
                            componentsProps={{
                                tooltip: {
                                    sx: {
                                        maxWidth: '300px'
                                    },
                                },
                            }}
                            slotProps={{
                                popper: {
                                    modifiers: [
                                        {
                                            name: 'offset',
                                            options: {
                                                offset: [0, -15],
                                            },
                                        },
                                    ],
                                },
                            }}
                            title={
                                <Fragment>
                                    <Box sx={{fontSize: "13px"}}>
                                        Difference in trading result
                                    </Box>
                                </Fragment>
                            }
                        >
                            <TableCell
                                align="right"
                                style={{ fontWeight: 'bold', fontSize: 12, padding: tablePadding }}
                            >
                                Disbalance
                            </TableCell>
                        </Tooltip>
                        <Tooltip
                            placement="bottom-start"
                            arrow
                            componentsProps={{
                                tooltip: {
                                    sx: {
                                        maxWidth: '300px'
                                    },
                                },
                            }}
                            slotProps={{
                                popper: {
                                    modifiers: [
                                        {
                                            name: 'offset',
                                            options: {
                                                offset: [0, -15],
                                            },
                                        },
                                    ],
                                },
                            }}
                            title={
                                <Fragment>
                                    <Box sx={{fontSize: "13px"}}>
                                        Necessary order size for balancing portfolio trading result
                                    </Box>
                                </Fragment>
                            }
                        >
                            <TableCell
                                align="right"
                                style={{ fontWeight: 'bold', fontSize: 12, padding: tablePadding }}
                            >
                                Bal order
                            </TableCell>
                        </Tooltip>
                        <Tooltip
                            placement="bottom-start"
                            arrow
                            componentsProps={{
                                tooltip: {
                                    sx: {
                                        maxWidth: '300px'
                                    },
                                },
                            }}
                            slotProps={{
                                popper: {
                                    modifiers: [
                                        {
                                            name: 'offset',
                                            options: {
                                                offset: [0, -15],
                                            },
                                        },
                                    ],
                                },
                            }}
                            title={
                                <Fragment>
                                    <Box sx={{fontSize: "13px"}}>
                                        Balanced trading result between the current price and the price on the 'Short price' or 'Long price' point after balancing portfolio
                                    </Box>
                                </Fragment>
                            }
                        >
                            <TableCell
                                align="right"
                                style={{ fontWeight: 'bold', fontSize: 12, padding: tablePadding }}
                            >
                                Short bal
                            </TableCell>
                        </Tooltip>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {models.map((row) => (
                        <TableRow
                            key={row.deltaPrice}
                            sx={{
                                '&:last-child td, &:last-child th': { border: 0 },
                            }}
                            style={{...(row.multiplier === 0
                                    ? { 
                                        backgroundColor: 'rgb(253,197,0)',
                                    }
                                    : {})}}
                        >
                            <TableCell
                                align="right" style={{ padding: tablePadding }}
                            >
                                {row.multiplier == 0
                                    ? "Current"
                                    : row.deltaPrice.toFixed(0)}
                            </TableCell>
                            <TableCell
                                align="right" style={{ padding: tablePadding }}
                            >
                                {row.multiplier == 0
                                    ? ""
                                    : row.shortPrice.toFixed(0)}
                            </TableCell>
                            <TableCell
                                align="right" style={{ padding: tablePadding }}
                            >
                                {row.multiplier == 0
                                    ? ""
                                    : row.longPrice.toFixed(0)}
                            </TableCell>
                            <TableCell
                                align="right" style={{ padding: tablePadding }}
                            >
                                {row.currentShortBalance.toFixed(0)}
                            </TableCell>
                            <TableCell
                                align="right" style={{ padding: tablePadding }}
                            >
                                {row.expirationLongBalance.toFixed(0)}
                            </TableCell>
                            <TableCell
                                align="right" style={{ padding: tablePadding }}
                            >
                                {row.multiplier == 0
                                    ? ""
                                    : row.currentShortDelta.toFixed(0)}
                            </TableCell>
                            <TableCell
                                align="right" style={{ padding: tablePadding }}
                            >
                                {row.multiplier == 0
                                    ? ""
                                    : row.expirationLongDelta.toFixed(0)}
                            </TableCell>
                            <TableCell
                                align="right" style={{ padding: tablePadding }}
                            >
                                {row.multiplier == 0
                                    ? ""
                                    : row.disbalance.toFixed(0)}
                            </TableCell>
                            <TableCell
                                align="right" style={{ padding: tablePadding }}
                            >
                                {row.multiplier == 0
                                    ? ""
                                    : row.balancingOrderSize.toFixed(3)}
                            </TableCell>
                            <TableCell
                                align="right" style={{ padding: tablePadding }}
                            >
                                {row.multiplier == 0
                                    ? ""
                                    : row.shortBalance.toFixed(0)}
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
}